import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ROUTES } from "@/config/routes.ts";
import { useGetCategories, useGetGenres } from "@/hooks/api/content.hooks.ts";

import { useIsKids } from "../useIsKids";
import { useLang } from "../useLang";

interface FooterLink {
  id: number;
  name: string;
  links: {
    id: number;
    name: string;
    link: string;
  }[];
}

export const useFooterLinks = (): FooterLink[] => {
  const { t } = useTranslation();
  const lang = useLang();
  const { data: categories } = useGetCategories();
  const { data: genres } = useGetGenres(12);
  const isKids = useIsKids();
  const token = localStorage.getItem("splay:token");

  const defaultLinks: FooterLink[] = [
    {
      id: 3,
      name: "Contacts.feedback",
      links: [
        {
          id: 1,
          name: "contacts.ques-and-ans",
          link: ROUTES.CONTACTS,
        },
        {
          id: 2,
          name: "contacts.contacts",
          link: ROUTES.CONTACTS,
        },
      ],
    },
  ];

  if (token) {
    defaultLinks[0].links.push({
      id: 3,
      name: "contacts.device",
      link: ROUTES.STAFF.SESSIONS,
    });
  }

  const [links, setLinks] = useState<FooterLink[]>(defaultLinks);

  useEffect(() => {
    if (categories && !isKids) {
      setLinks((prev) => [
        ...prev.filter((link) => link.id !== 1),
        {
          id: 1,
          name: "catalog",
          links: [
            ...categories.map((category) => ({
              id: category.id,
              name: category.name,
              link: ROUTES.MAIN.CATALOG.replace(":id", category.id.toString()),
            })),
            {
              id: categories.length + 1,
              name: "General.tv-channels",
              link: ROUTES.CHANNELS.MAIN,
            },
          ],
        },
      ]);
    } else {
      setLinks(() => [
        ...defaultLinks,
        {
          id: 1,
          name: "catalog",
          links: [
            {
              id: defaultLinks.length + 1,
              name: "General.tv-channels",
              link: ROUTES.CHANNELS.MAIN,
            },
          ],
        },
      ]);
    }
  }, [t, categories, isKids, lang]);

  useEffect(() => {
    if (genres && !isKids) {
      setLinks((prev) => [
        ...prev.filter((link) => link.id !== 2),
        {
          id: 2,
          name: "genres.genres",
          links: genres.pages[0].slice(0, 6).map((genre) => ({
            id: genre.id,
            name: genre.name,
            link: ROUTES.MAIN.GENRE.replace(":id", genre.id.toString()),
          })),
        },
      ]);
    } else {
      setLinks(() => [
        ...defaultLinks,
        {
          id: 1,
          name: "catalog",
          links: [
            {
              id: defaultLinks.length + 1,
              name: "General.tv-channels",
              link: ROUTES.CHANNELS.MAIN,
            },
          ],
        },
      ]);
    }
  }, [t, genres, isKids, lang]);

  return links.sort((a, b) => a.id - b.id);
};
